import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BadgePercent,
  Clock3,
  Gift,
  Megaphone,
  Percent,
  Sparkles,
  Users,
  Wallet,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import '../../styles/module-shared.css';
import {
  PROMO_CATEGORIES,
  buildFeaturedPromotion,
  endsInLabel,
  filterCatalogByCategory,
  formatMoney,
  formatPromoDate,
  getCountdown,
  normalizeCatalogList,
  normalizeMyPromotionList,
  sortCatalog,
  splitMyPromotions,
  withClaimState,
} from './promotionsData';
import './promotions.css';

const THEME = getThemeColor('primary');

const claimButtonLabel = (promo, claimingId) => {
  if (claimingId === String(promo.promotionId)) return 'Claiming…';
  if (promo.alreadyClaimed) return 'Already Claimed';
  if (promo.available) return 'Claim Now';
  return promo.cta || 'Unavailable';
};
const FILTER_ICONS = {
  all: Megaphone,
  deposit: Wallet,
  cashback: BadgePercent,
  rebate: Percent,
  contest: Sparkles,
  referral: Users,
  special: Gift,
};

const PromotionList = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [mine, setMine] = useState([]);
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('recommended');
  const [nowTick, setNowTick] = useState(Date.now());
  const [claimingId, setClaimingId] = useState(null);

  const fetchData = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const [catalogRes, mineRes] = await Promise.all([
        apiService.getAffiliatePromotions(),
        apiService.getMyAffiliatePromotions(),
      ]);
      if (cancelled()) return;

      if (!catalogRes.success && !mineRes.success) {
        setError(catalogRes.error || mineRes.error || 'Failed to load promotions');
      } else if (!catalogRes.success && catalogRes.error) {
        setError(catalogRes.error);
      }

      setCatalog(catalogRes.success ? normalizeCatalogList(catalogRes.data) : []);
      setMine(mineRes.success ? normalizeMyPromotionList(mineRes.data) : []);
    } catch (err) {
      if (!cancelled()) setError(err.message || 'Failed to load promotions');
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('promotions-list-v1', fetchData, true);

  const handleClaim = useCallback(
    async (promo) => {
      if (promo?.alreadyClaimed || !promo?.available) {
        showError('This promotion is already claimed');
        return;
      }
      const affiliatePromotionId = Number(promo?.promotionId ?? promo?.id);
      if (!Number.isFinite(affiliatePromotionId) || affiliatePromotionId <= 0) {
        showError('Invalid promotion');
        return;
      }
      if (claimingId) return;

      setClaimingId(String(affiliatePromotionId));
      try {
        const res = await apiService.claimAffiliatePromotion(affiliatePromotionId);
        if (!res.success) {
          showError(res.error || res.message || 'Failed to claim promotion');
          return;
        }

        const name =
          res.data?.promotionName ||
          res.data?.PromotionName ||
          promo?.title ||
          'Promotion';
        const status = res.data?.status || res.data?.Status || 'Pending';
        showSuccess(`${name} claimed — status: ${status}`);
        await fetchData({ cancelled: () => false });
        navigate('/promotions/my');
      } catch (err) {
        showError(err.message || 'Failed to claim promotion');
      } finally {
        setClaimingId(null);
      }
    },
    [claimingId, fetchData, navigate, showError, showSuccess]
  );

  const catalogWithClaims = useMemo(() => withClaimState(catalog, mine), [catalog, mine]);

  const featured = useMemo(() => buildFeaturedPromotion(catalogWithClaims), [catalogWithClaims]);
  const countdown = useMemo(
    () => getCountdown(featured?.endsAt),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [featured?.endsAt, nowTick]
  );

  useEffect(() => {
    if (!featured?.endsAt) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [featured?.endsAt]);

  const filtered = useMemo(
    () => sortCatalog(filterCatalogByCategory(catalogWithClaims, category), sortBy),
    [catalogWithClaims, category, sortBy]
  );

  const { active: activeRows, history: historyRows } = useMemo(
    () => splitMyPromotions(mine),
    [mine]
  );

  return (
    <div className="page promo-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={Megaphone}
          title="Promotions"
          subtitle="Browse active offers, claim bonuses, and track promotion progress"
          iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
        />
        <ModuleAlert message={error} />

        {loading ? (
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        ) : (
          <>
            {featured ? (
              <section className="promo-featured">
                <div className="promo-featured__left">
                  <span className="promo-featured__tag">{featured.status}</span>
                  <h2>{featured.title}</h2>
                  <p>{featured.subtitle}</p>
                  {featured.available ? (
                    <button
                      type="button"
                      className="promo-featured__cta"
                      disabled={Boolean(claimingId)}
                      onClick={() => handleClaim(featured)}
                    >
                      {claimButtonLabel(featured, claimingId)}
                    </button>
                  ) : (
                    <button type="button" className="promo-featured__cta" disabled>
                      {featured.alreadyClaimed ? 'Already Claimed' : featured.cta}
                    </button>
                  )}
                  <div className="promo-featured__gift" aria-hidden="true">
                    <Gift className="w-7 h-7" />
                  </div>
                </div>
                <div className="promo-featured__right">
                  <div className="promo-featured__row">
                    <div className="promo-featured__row-icon">
                      <Percent className="w-4 h-4" />
                    </div>
                    <div>
                      <span>{featured.bonusLabel}</span>
                      <strong>{featured.bonusValue}</strong>
                    </div>
                  </div>
                  <div className="promo-featured__row">
                    <div className="promo-featured__row-icon">
                      <Clock3 className="w-4 h-4" />
                    </div>
                    <div>
                      <span>Offer ends in</span>
                      <div className="promo-countdown">
                        {[
                          ['Days', countdown.days],
                          ['Hours', countdown.hours],
                          ['Mins', countdown.minutes],
                          ['Secs', countdown.seconds],
                        ].map(([label, value]) => (
                          <div key={label} className="promo-countdown__box">
                            <strong>{String(value).padStart(2, '0')}</strong>
                            <em>{label}</em>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="promo-featured__row">
                    <div className="promo-featured__row-icon">
                      <Users className="w-4 h-4" />
                    </div>
                    <div>
                      <span>Eligible accounts</span>
                      <strong>{featured.eligible}</strong>
                    </div>
                  </div>
                  {featured.minimumDeposit > 0 ? (
                    <div className="promo-featured__row">
                      <div className="promo-featured__row-icon">
                        <Wallet className="w-4 h-4" />
                      </div>
                      <div>
                        <span>Minimum deposit</span>
                        <strong>{formatMoney(featured.minimumDeposit)}</strong>
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>
            ) : (
              <div className="promo-table-card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                <p className="promo-empty" style={{ margin: 0 }}>
                  No featured promotions available right now.
                </p>
              </div>
            )}

            <div className="promo-filters" role="tablist" aria-label="Promotion categories">
              {PROMO_CATEGORIES.map((item) => {
                const Icon = FILTER_ICONS[item.id] || Megaphone;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`promo-filter${category === item.id ? ' is-active' : ''}`}
                    onClick={() => setCategory(item.id)}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {item.label}
                  </button>
                );
              })}
            </div>

            <div className="promo-sort">
              <span>Sort by:</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="recommended">Recommended</option>
                <option value="ending">Ending soon</option>
                <option value="newest">Newest</option>
                <option value="name">Name</option>
              </select>
            </div>

            {filtered.length === 0 ? (
              <div className="promo-table-card" style={{ padding: '2rem', textAlign: 'center' }}>
                <p className="promo-empty" style={{ margin: 0 }}>
                  No promotions found in this category.
                </p>
              </div>
            ) : (
              <div className="promo-grid">
                {filtered.map((promo) => (
                  <article key={promo.id} className="promo-card">
                    <div className="promo-card__top" style={{ background: promo.tone }} />
                    <div className="promo-card__body">
                      <div className="promo-card__gift" style={{ background: promo.tone }}>
                        <Gift className="w-5 h-5" />
                      </div>
                      <h3>{promo.title}</h3>
                      {promo.description ? (
                        <p className="promo-card__desc">{promo.description}</p>
                      ) : null}
                      <span
                        className={`promo-card__status ${
                          promo.lifecycle === 'active'
                            ? 'is-active'
                            : promo.lifecycle === 'upcoming'
                              ? 'is-upcoming'
                              : 'is-expired'
                        }`}
                      >
                        {promo.lifecycle === 'active'
                          ? 'Active'
                          : promo.lifecycle === 'upcoming'
                            ? 'Upcoming'
                            : 'Expired'}
                      </span>
                      <div className="promo-card__meta">
                        {promo.minimumDeposit > 0
                          ? `Min deposit ${formatMoney(promo.minimumDeposit)}`
                          : null}
                        {promo.bonusPercent > 0 ? ` · ${promo.bonusPercent}% bonus` : null}
                        {promo.validUntil ? ` · Until ${formatPromoDate(promo.validUntil)}` : null}
                      </div>
                      <button
                        type="button"
                        className="promo-card__btn"
                        style={{ background: promo.alreadyClaimed ? '#9ca3af' : promo.tone }}
                        disabled={!promo.available || Boolean(claimingId)}
                        onClick={() => promo.available && handleClaim(promo)}
                      >
                        {claimButtonLabel(promo, claimingId)}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}

            <div className="promo-tables">
              <section className="promo-table-card">
                <div className="promo-table-card__head">
                  <h3>Active Promotions</h3>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="promo-table">
                    <thead>
                      <tr>
                        <th>Promotion</th>
                        <th>Type</th>
                        <th>Progress</th>
                        <th>Ends in</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeRows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="promo-empty">
                            No active promotions.
                          </td>
                        </tr>
                      ) : (
                        activeRows.map((row) => (
                          <tr key={row.id}>
                            <td>{row.title}</td>
                            <td>{row.typeName}</td>
                            <td>
                              <div className="promo-progress">
                                <span>{row.progressLabel}</span>
                                <div className="promo-progress__bar">
                                  <i style={{ width: `${row.progressPct}%` }} />
                                </div>
                              </div>
                            </td>
                            <td>{endsInLabel(row.endDate)}</td>
                            <td>
                              <Link className="promo-link" to="/promotions/my">
                                View
                              </Link>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="promo-table-card__foot">
                  <Link className="promo-link" to="/promotions/my">
                    View all active promotions
                  </Link>
                </div>
              </section>

              <section className="promo-table-card">
                <div className="promo-table-card__head">
                  <h3>Promotion History</h3>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="promo-table">
                    <thead>
                      <tr>
                        <th>Promotion</th>
                        <th>Type</th>
                        <th>Period</th>
                        <th>Status</th>
                        <th>Benefit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyRows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="promo-empty">
                            No promotion history yet.
                          </td>
                        </tr>
                      ) : (
                        historyRows.map((row) => (
                          <tr key={row.id}>
                            <td>{row.title}</td>
                            <td>{row.typeName}</td>
                            <td>{row.period}</td>
                            <td>{row.statusLabel}</td>
                            <td>{row.bonusEarned}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="promo-table-card__foot">
                  <Link className="promo-link" to="/promotions/my">
                    View all history
                  </Link>
                </div>
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PromotionList;
